import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { useMicrosoftAuth } from '../../hooks/useMicrosoftAuth';
import { X } from 'lucide-react';
import { getConfig } from '../../utils/config';
import AgreementCard from '../agreements/AgreementCard';
import TwoFactorVerification from './TwoFactorVerification';
import ForceSetupTwoFactor from './ForceSetupTwoFactor';

function SignUp() {
    const [formData, setFormData] = useState({
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        password: '',
        confirmPassword: '',
        website: '' // honeypot, always empty for real users, bots fill every field
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    // User agreements, fetched lazily when the agreement modal opens
    const [showAgreementModal, setShowAgreementModal] = useState(false);
    const [agreementsToSign, setAgreementsToSign] = useState([]);
    const [agreementsLoading, setAgreementsLoading] = useState(false);
    const [agreementsError, setAgreementsError] = useState(null);
    const [acks, setAcks] = useState({});

    const [microsoftLoading, setMicrosoftLoading] = useState(false);

    // 2FA flow state (mirrors the login page) for social sign-ups.
    const [requires2FA, setRequires2FA] = useState(false);
    const [tempToken, setTempToken] = useState(null);
    const [otpMethod, setOtpMethod] = useState(null);
    const [secondaryMethod, setSecondaryMethod] = useState(null);
    const [secondaryDestinationMask, setSecondaryDestinationMask] = useState(null);
    const [deviceTrust, setDeviceTrust] = useState({ enabled: false, days: null });
    const [requires2FASetup, setRequires2FASetup] = useState(false);
    const [setupToken, setSetupToken] = useState(null);
    const [setupUser, setSetupUser] = useState(null);

    const { signup, googleLogin, completeTwoFactor, isAuthenticated, loading } = useAuth();
    const { signInWithMicrosoft } = useMicrosoftAuth();
    const navigate = useNavigate();

    // Same shape as the login page's handler, so both social paths here carry
    // every field of the challenge, including the device-trust offer.
    const applyTwoFactorChallenge = (result) => {
        setRequires2FA(true);
        setTempToken(result.temp_token);
        setOtpMethod(result.otp_method);
        setSecondaryMethod(result.secondary_method);
        setSecondaryDestinationMask(result.secondary_destination_mask);
        setDeviceTrust({
            enabled: Boolean(result.device_trust_enabled),
            days: result.device_trust_days ?? null,
        });
    };

    // Custom Google button (matches the login page) instead of Google's pill widget.
    const customGoogleLogin = useGoogleLogin({
        ux_mode: 'popup',
        select_account: true,
        onSuccess: (tokenResponse) => {
            setGoogleLoading(true);
            setError('');
            if (googleLogin) {
                googleLogin({ credential: tokenResponse.access_token })
                    .then(result => {
                        if (result.success) {
                            if (result.requires_2fa) {
                                applyTwoFactorChallenge(result);
                            } else if (result.requires_2fa_setup) {
                                setRequires2FASetup(true);
                                setSetupToken(result.setup_token);
                                setSetupUser(result.user);
                            } else {
                                localStorage.removeItem('jpro_login_redirect');
                                navigate(from, { replace: true });
                            }
                        } else {
                            setError(result.error || 'Google signup failed');
                        }
                    })
                    .catch(err => {
                        setError('An error occurred during Google signup');
                        console.error('Google signup error:', err);
                    })
                    .finally(() => setGoogleLoading(false));
            } else {
                setError('Google signup is not configured');
                setGoogleLoading(false);
            }
        },
        onError: () => {
            setError('Google signup failed. Please try again.');
            setGoogleLoading(false);
        }
    });

    const handleMicrosoftSignup = async () => {
        try {
            setMicrosoftLoading(true);
            setError('');
            const result = await signInWithMicrosoft();

            if (result.requires_2fa) {
                applyTwoFactorChallenge(result);
                return;
            }

            if (result.requires_2fa_setup) {
                setRequires2FASetup(true);
                setSetupToken(result.setup_token);
                setSetupUser(result.user);
                return;
            }

            const finalAccessToken = result.access_token || result.token;
            if (finalAccessToken) {
                completeTwoFactor(result);
                localStorage.removeItem('jpro_login_redirect');
                navigate(from, { replace: true });
            } else {
                setError('Microsoft signup failed. Please try again.');
            }
        } catch (err) {
            setError(err.message || 'Microsoft signup failed. Please try again.');
            console.error('Microsoft signup error:', err);
        } finally {
            setMicrosoftLoading(false);
        }
    };

    useEffect(() => {
        if (!showAgreementModal || agreementsToSign.length > 0) return;
        const API_URL = getConfig('API_URL');
        setAgreementsLoading(true);
        setAgreementsError(null);
        (async () => {
            try {
                const resp = await fetch(`${API_URL}/public/agreements/required?audience=all`);
                const data = await resp.json();
                const list = data.agreements || [];
                if (list.length === 0) {
                    setAgreementsError('Agreement system not configured. Please contact support.');
                } else {
                    setAgreementsToSign(list);
                }
            } catch (_e) {
                setAgreementsError('Could not load user agreements. Please try again.');
            } finally {
                setAgreementsLoading(false);
            }
        })();
    }, [showAgreementModal, agreementsToSign.length]);

    const allRequiredChecked = () => {
        return agreementsToSign.every((a) => {
            const picked = acks[a.agreement_version_id] || {};
            if (a.acknowledgment_mode === 'single') return !!picked.__single__;
            return (a.acknowledgment_items || [])
                .filter((i) => i.required)
                .every((i) => !!picked[i.id]);
        });
    };

    const buildAcceptedAgreementsPayload = () => {
        return agreementsToSign.map((a) => {
            const picked = acks[a.agreement_version_id] || {};
            const accepted_items =
                a.acknowledgment_mode === 'single'
                    ? []
                    : Object.keys(picked).filter((k) => picked[k]);
            return { agreement_version_id: a.agreement_version_id, accepted_items };
        });
    };

    // After signup, honor an invite return path (e.g. a class-seat invitation)
    // before falling back to the dashboard. Peek only, the key is cleared once
    // in the redirect effect below, so a later navigate cannot lose it.
    const from = (() => {
        const r = localStorage.getItem('jpro_login_redirect');
        // Only honor internal paths, reject external and protocol-relative
        // ("//evil.com") redirects, which would be an open-redirect.
        return (typeof r === 'string' && r.startsWith('/') && !r.startsWith('//')) ? r : '/dashboard';
    })();

    // Redirect authenticated users to dashboard (or their invite return path)
    useEffect(() => {
        if (!loading && isAuthenticated) {
            localStorage.removeItem('jpro_login_redirect');
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, loading, navigate, from]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.username || !formData.email || !formData.password) {
            setError('Please fill in all required fields.');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (formData.phone && !/^\d{10,15}$/.test(formData.phone)) {
            setError('Please enter a valid phone number (10-15 digits).');
            return;
        }

        setError('');
        setShowAgreementModal(true);
    };

    const handleAgreeAndSignup = async () => {
        try {
            setError('');
            setIsLoading(true);

            const signupData = {
                ...formData,
                passwordConfirmation: formData.confirmPassword,
                acceptedAgreements: buildAcceptedAgreementsPayload()
            };

            const result = await signup(signupData);

            if (result.success) {
                localStorage.removeItem('jpro_login_redirect');
                navigate(from, { replace: true });
            } else {
                setError(result.error || 'Registration failed. Please try again.');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
            console.error('Signup error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handle2FASuccess = (data) => {
        completeTwoFactor(data);
        localStorage.removeItem('jpro_login_redirect');
        navigate(from, { replace: true });
    };

    const handle2FABack = (reason) => {
        setRequires2FA(false);
        setTempToken(null);
        setOtpMethod(null);
        setSecondaryMethod(null);
        setSecondaryDestinationMask(null);
        setError(typeof reason === 'string' ? reason : '');
    };

    // Social sign-up authenticated an account that already has 2FA enrolled:
    // verify the code before continuing.
    if (requires2FA) {
        return (
            <TwoFactorVerification
                tempToken={tempToken}
                otpMethod={otpMethod}
                secondaryMethod={secondaryMethod}
                secondaryDestinationMask={secondaryDestinationMask}
                deviceTrustEnabled={deviceTrust.enabled}
                deviceTrustDays={deviceTrust.days}
                onSuccess={handle2FASuccess}
                onBack={handle2FABack}
            />
        );
    }

    // Org policy requires 2FA and this account has not enrolled yet: run the
    // setup wizard, then send them to sign in with their newly enabled 2FA.
    if (requires2FASetup) {
        return (
            <ForceSetupTwoFactor
                setupToken={setupToken}
                user={setupUser}
                onBack={() => {
                    setRequires2FASetup(false);
                    setSetupToken(null);
                    setSetupUser(null);
                    setError('');
                }}
                onComplete={() => navigate('/login', {
                    state: { notice: 'Two-factor authentication enabled. Sign in to continue.' },
                })}
            />
        );
    }

    return (
        <div className="w-full max-w-4xl mx-auto py-10 sm:py-14">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-blue-900/10 dark:border-gray-700 dark:bg-gray-800 dark:shadow-black/40 lg:grid lg:grid-cols-5">

                {/* Brand panel: deep blue field, always dark in both themes */}
                <div className="relative overflow-hidden bg-[#0B1B3B] px-8 py-10 sm:px-10 lg:col-span-2 lg:flex lg:flex-col lg:justify-between lg:py-12">
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl"
                    />
                    <div className="relative">
                        <p className="font-unna text-xs uppercase tracking-[0.25em] text-blue-300">
                            Zeffert &amp; Associates
                        </p>
                        <h1 className="mt-3 font-dm-serif text-5xl text-white lg:text-6xl">
                            JPRO
                        </h1>
                        <div className="mt-5 h-px w-12 bg-orange-400" />
                        <p className="mt-5 max-w-xs font-rasa text-lg leading-relaxed text-blue-100/90">
                            The intelligence platform for affordable housing compliance, audits, and training.
                        </p>
                    </div>

                    {/* Signature: AIT income designation bands, 60 set in orange */}
                    <div aria-hidden="true" className="relative mt-10 hidden select-none lg:block">
                        <p className="flex items-baseline justify-between font-dm-serif text-2xl text-blue-200/20">
                            <span>20</span>
                            <span>30</span>
                            <span>40</span>
                            <span>50</span>
                            <span className="text-orange-400/60">60</span>
                            <span>70</span>
                            <span>80</span>
                        </p>
                        <p className="mt-3 font-unna text-[10px] uppercase tracking-[0.3em] text-blue-200/40">
                            Average Income designations
                        </p>
                    </div>
                </div>

                {/* Form panel */}
                <div className="px-6 py-8 sm:px-10 sm:py-10 lg:col-span-3">
                    <h2 className="font-unna text-3xl font-bold text-gray-900 dark:text-white">
                        Create your account
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Username, email, and password are required.
                    </p>

                    {error && (
                        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                            {error}
                        </div>
                    )}

                    <form className="mt-6" onSubmit={handleSubmit}>
                        {/* Honeypot: visually hidden to humans (offscreen + no tab focus + aria-hidden),
                            but present in the DOM for field-filling bots. Real users never fill this.
                            Do NOT use display:none or visibility:hidden, some bots detect that. */}
                        <div
                            aria-hidden="true"
                            style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                        >
                            <label htmlFor="website">Website (leave blank)</label>
                            <input
                                type="text"
                                id="website"
                                name="website"
                                tabIndex="-1"
                                autoComplete="off"
                                value={formData.website}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="username"
                                className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                            >
                                Username
                            </label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                            />
                        </div>
                        <div className="mt-5 grid gap-5 sm:grid-cols-2 sm:gap-4">
                            <div>
                                <label
                                    htmlFor="firstName"
                                    className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                                >
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="lastName"
                                    className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                                >
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                                />
                            </div>
                        </div>
                        <div className="mt-5">
                            <label
                                htmlFor="email"
                                className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                            >
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                            />
                        </div>
                        <div className="mt-5">
                            <label
                                htmlFor="phone"
                                className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                            >
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="e.g., 1234567890"
                                className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-400"
                            />
                        </div>
                        <div className="mt-5 grid gap-5 sm:grid-cols-2 sm:gap-4">
                            <div>
                                <label
                                    htmlFor="city"
                                    className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                                >
                                    City
                                </label>
                                <input
                                    type="text"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="state"
                                    className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                                >
                                    State
                                </label>
                                <input
                                    type="text"
                                    id="state"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    placeholder="e.g., CA"
                                    className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-400"
                                />
                            </div>
                        </div>
                        <div className="mt-5 grid gap-5 sm:grid-cols-2 sm:gap-4">
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                                >
                                    Password
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="confirmPassword"
                                    className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                                >
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                                />
                            </div>
                        </div>
                        <div className="mt-7 flex justify-center">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium tracking-wide text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-blue-500 dark:hover:bg-blue-400 dark:focus:ring-offset-gray-800"
                            >
                                {isLoading ? 'Creating account...' : 'Sign up'}
                            </button>
                        </div>
                    </form>
                    <div className="mt-7 flex items-center gap-4">
                        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                        <span className="font-unna text-xs uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                            or sign up with
                        </span>
                        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                    </div>
                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        <button
                            type="button"
                            onClick={customGoogleLogin}
                            disabled={googleLoading}
                            className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800"
                        >
                            {googleLoading ? (
                                <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700 dark:border-gray-600 dark:border-t-gray-200"></div>
                            ) : (
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                            )}
                            {googleLoading ? 'Connecting...' : 'Continue with Google'}
                        </button>

                        <button
                            type="button"
                            onClick={handleMicrosoftSignup}
                            disabled={microsoftLoading}
                            className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800"
                        >
                            {microsoftLoading ? (
                                <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700 dark:border-gray-600 dark:border-t-gray-200"></div>
                            ) : (
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23">
                                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                                </svg>
                            )}
                            {microsoftLoading ? 'Connecting...' : 'Continue with Microsoft'}
                        </button>
                    </div>
                    <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        {" "}
                        Have an account?{" "}
                        <Link
                            to="/login"
                            className="font-medium text-orange-600 hover:underline dark:text-orange-300"
                        >
                            Login
                        </Link>
                    </p>
                </div>
            </div>

            {showAgreementModal && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-xl shadow-blue-900/10 dark:shadow-black/40 flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h2 className="font-unna text-xl font-bold text-gray-900 dark:text-white">
                                Review and accept agreements
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowAgreementModal(false)}
                                disabled={isLoading}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {agreementsLoading ? (
                                <div className="text-sm text-gray-500 dark:text-gray-400">Loading agreements…</div>
                            ) : agreementsError ? (
                                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                                    {agreementsError}
                                </div>
                            ) : (
                                agreementsToSign.map((a) => (
                                    <AgreementCard
                                        key={a.agreement_version_id}
                                        agreement={a}
                                        checkedItems={acks[a.agreement_version_id] || {}}
                                        onChange={(items) =>
                                            setAcks((prev) => ({ ...prev, [a.agreement_version_id]: items }))
                                        }
                                    />
                                ))
                            )}
                            {error && (
                                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowAgreementModal(false)}
                                disabled={isLoading}
                                className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAgreeAndSignup}
                                disabled={isLoading || agreementsLoading || !!agreementsError || !allRequiredChecked()}
                                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Creating account…' : 'Agree & create account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SignUp;